/**
 * COMPLIANCE DASHBOARD
 * 
 * Main dashboard for the Compliance module showing:
 * - Overall compliance health score
 * - Key metrics with trends (audits, training, action items)
 * - Audit score trends (chart)
 * - Training completion trends (chart)
 * - Store performance comparison
 * - Critical alerts and quick actions
 */

import React, { useEffect, useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
  Stack,
  useTheme,
  useMediaQuery,
  LinearProgress,
  Chip,
  SvgIcon,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  School as SchoolIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Store as StoreIcon,
  Assignment as AssignmentIcon,
  Speed as SpeedIcon,
  FilterList as FilterListIcon,
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { complianceService } from '../../../app/services/api/compliance/complianceService';
import { DashboardMetrics, AuditScoreTrend, TrainingCompletionTrend, Store, Region, ActionItem } from '../../../data/complianceMockData';
import { getTrainingClient, TrainingDashboardMetricsDto, TrainingCompletionTrendDto, StoreTrainingPerformanceDto } from '../../../app/services/api/compliance/training';
import { UserContext } from '../../../components/shared/useUser';
import PageArea from '../../../components/shared/PageArea';
import PageTitle from '../../../components/shared/PageTitle';
import { useNavigate, useSearchParams } from 'react-router-dom';
import CustomPagination from '../../../components/forms/Pagination';
import { Section } from '../../../components/ui/Section';
import { Pill } from '../../../components/shared/Pill';
import ScrollableTableCell from '../../../components/shared/ScrollableTableCell';


// Metric Card Component with Trend
interface MetricCardProps {
  title: string;
  value: number | string;
  subtitle?: string;
  icon: React.ReactNode;
  color: string;
  trend?: number; // % change
  trendLabel?: string;
}

const MetricCard: React.FC<MetricCardProps> = ({ title, value, subtitle, icon, color, trend, trendLabel }) => {
  const theme = useTheme();

  return (
    <Section style={{ padding: 5, marginBottom: 5 }}>
      <Box
        display="flex"
        alignItems="stretch"
        justifyContent="space-between"
      >
        <Box
          flex={1}
          minWidth={0}
          sx={{ mr: 1 }}
        >
          <Box
            sx={{
              color: theme.palette.custom.gray[600],
              fontSize: '10px',
              textTransform: 'uppercase',
              mb: 0.25,
              fontWeight: 500,
            }}
          >
            {title}
          </Box>
          <Box sx={{
            fontSize: '20px',
            fontWeight: 600,
            color: theme.palette.custom.gray[900],
            mb: 0.25,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {value}
          </Box>
          {subtitle && (
            <Box
              sx={{
                fontSize: '12px',
                color: theme.palette.custom.gray[600],
              }}
            >
              {subtitle}
            </Box>
          )}
          {trend !== undefined && (
            <Box
              display="flex"
              alignItems="center"
              mt={0.5}
            >
              <SvgIcon sx={{ fontSize: 16, mr: 0.25, color: trend >= 0 ? '#2e7d32' : '#d32f2f' }}>
                {trend >= 0 ? <TrendingUpIcon /> : <TrendingDownIcon />}
              </SvgIcon>
              <Box
                sx={{
                  fontSize: '11px',
                  fontWeight: 500,
                  color: trend >= 0 ? '#2e7d32' : '#d32f2f',
                }}
              >
                {trend > 0 ? '+' : ''}{trend.toFixed(1)}%
              </Box>
              {trendLabel && (
                <Box
                  sx={{
                    fontSize: '11px',
                    color: theme.palette.custom.gray[600],
                    ml: 0.5,
                  }}
                >
                  {trendLabel}
                </Box>
              )}
            </Box>
          )}
        </Box>
        <Box
          sx={{
            backgroundColor: `${color}20`,
            borderRadius: '50%',
            width: 48,
            height: 48,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: color,
            flexShrink: 0,
          }}
        >
          <SvgIcon fontSize="medium">
            {icon}
          </SvgIcon>
        </Box>
      </Box>
    </Section>
  );
};

// Compliance Health Score Card
interface HealthScoreCardProps {
  score: number;
  riskLevel: 'Low' | 'Medium' | 'High' | 'Critical';
  criticalIssues: number;
}

const HealthScoreCard: React.FC<HealthScoreCardProps> = ({ score, riskLevel, criticalIssues }) => {
  const theme = useTheme();
  const { t } = useTranslation('complianceAudits');

  const getRiskColor = (risk: string) => {
    switch (risk) {
      case 'Low': return '#2e7d32';
      case 'Medium': return '#ed6c02';
      case 'High': return '#f57c00';
      case 'Critical': return '#d32f2f';
      default: return '#1976d2';
    }
  };

  const getRiskBackgroundColor = (risk: string) => {
    switch (risk) {
      case 'Low': return '#e8f5e9';
      case 'Medium': return '#fff3e0';
      case 'High': return '#ffe0b2';
      case 'Critical': return '#ffebee';
      default: return '#e3f2fd';
    }
  };

  return (
    <Card sx={{ background: `linear-gradient(135deg, ${getRiskBackgroundColor(riskLevel)} 0%, #ffffff 100%)` }}>
      <CardContent>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
        >
          <Box sx={{ fontSize: '16px', fontWeight: 600, color: theme.palette.custom.gray[900] }}>
            {t('dashboard.overallComplianceHealth')}
          </Box>
          <Chip
            label={riskLevel}
            size="small"
            sx={{
              backgroundColor: getRiskColor(riskLevel),
              color: '#fff',
              fontWeight: 600,
            }}
          />
        </Box>

        <Box
          display="flex"
          alignItems="center"
          gap={3}
        >
          <Box
            position="relative"
            display="inline-flex"
          >
            <CircularProgress
              variant="determinate"
              value={score}
              size={100}
              thickness={6}
              sx={{ color: getRiskColor(riskLevel) }}
            />
            <Box
              sx={{
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                position: 'absolute',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
              }}
            >
              <Box sx={{ fontSize: '28px', fontWeight: 700, color: theme.palette.custom.gray[900] }}>
                {score.toFixed(0)}
              </Box>
              <Box sx={{ fontSize: '12px', color: theme.palette.custom.gray[600] }}>
                {t('common.score')}
              </Box>
            </Box>
          </Box>

          <Box flex={1}>
            <Box sx={{ fontSize: '14px', color: theme.palette.custom.gray[700], mb: 1 }}>
              {t('dashboard.complianceHealthBreakdown')}
            </Box>
            <LinearProgress
              variant="determinate"
              value={score}
              sx={{
                height: 8,
                borderRadius: 1,
                backgroundColor: theme.palette.custom.gray[200],
                '& .MuiLinearProgress-bar': {
                  backgroundColor: getRiskColor(riskLevel),
                },
              }}
            />
            <Box
              display="flex"
              justifyContent="space-between"
              mt={1}
            >
              <Box sx={{ fontSize: '13px', color: theme.palette.custom.gray[600] }}>
                {criticalIssues === 0 ? t('dashboard.noCriticalIssues') : t('dashboard.criticalIssueCount', { count: criticalIssues })}
              </Box>
              <Box sx={{ fontSize: '13px', fontWeight: 600, color: getRiskColor(riskLevel) }}>
                {score >= 90 ? t('dashboard.healthExcellent') : score >= 80 ? t('dashboard.healthGood') : score >= 70 ? t('dashboard.healthFair') : t('dashboard.healthNeedsAttention')}
              </Box>
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const ComplianceDashboard: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(`(max-width:${theme.breakpoints.values.md}px)`);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { selectedStore, selectedFranchise, userFranchisesCurrentTenant, userStoresCurrentTenant, hasPermissionTo } = useContext(UserContext);
  const trainingClient = getTrainingClient();
  const { t } = useTranslation('complianceAudits');
  const localeLanguage = navigator.languages?.[0] || navigator.language;

  // Optional ?view=audits|training param (used by the separate Audits/Training nav entries)
  // narrows which sections render below, on top of - never instead of - the real permission
  // checks. No param (the original combined nav entry) renders exactly as before.
  const viewMode = searchParams.get('view');

  // Check permissions for dashboard sections
  const canViewTraining = hasPermissionTo(['ViewTrainingDashboard']);
  const canViewAuditDashboard = hasPermissionTo(['ViewAuditDashboard']);
  const isStoreScoped = hasPermissionTo(['StoreScopedAuditsView']);
  const showAuditSections = canViewAuditDashboard && viewMode !== 'training';
  const showTrainingSections = canViewTraining && viewMode !== 'audits';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [auditTrends, setAuditTrends] = useState<AuditScoreTrend[]>([]);
  const [trainingTrends, setTrainingTrends] = useState<TrainingCompletionTrendDto[]>([]);

  // Training dashboard data
  const [trainingMetrics, setTrainingMetrics] = useState<TrainingDashboardMetricsDto | null>(null);
  const [storePerformance, setStorePerformance] = useState<StoreTrainingPerformanceDto[]>([]);

  // Filter states
  const [stores, setStores] = useState<Store[]>([]);
  const [regions, setRegions] = useState<Region[]>([]);
  const [filterType, setFilterType] = useState<'all' | 'stores' | 'regions'>('all');
  const [selectedStores, setSelectedStores] = useState<string[]>([]);
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);

  // Action items state
  const [actionItems, setActionItems] = useState<ActionItem[]>([]);
  const [actionItemsPage, setActionItemsPage] = useState(0);
  const actionItemsPerPage = 6;

  useEffect(() => {
    loadInitialData();
  }, [selectedStore?.storeNumber]);

  const filtersInitialisedRef = React.useRef(false);
  useEffect(() => {
    if (!filtersInitialisedRef.current) {
      filtersInitialisedRef.current = true;
      return;
    }
    loadDashboardData(false);
  }, [filterType, selectedStores, selectedRegions]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [storesData, regionsData, actionItemsData] = await Promise.all([
        complianceService.complianceStores.getAll(false, userFranchisesCurrentTenant.map(f => f.franchiseId as number)),
        complianceService.complianceRegions.getAll(userFranchisesCurrentTenant.map(f => f.franchiseId as number)),
        complianceService.actionItems.getAll(selectedStore?.storeNumber),
      ]);

      setStores(storesData);
      setRegions(regionsData);
      setActionItems(actionItemsData);

      // Load initial dashboard data
      await loadDashboardData(true); // true = initial load
    } catch (err) {
      setError(err instanceof Error ? err.message : t('dashboard.failedToLoadInitial'));
      setLoading(false);
    }
  };

  const loadDashboardData = async (isInitial: boolean = false) => {
    try {

      if (isInitial) {
        setLoading(true);
      }
      setError(null);

      // Do not send every accessible store by default.
      // Let API apply user scope when no explicit store/region filter is selected.
      let storeNumbers: string[] | undefined;
      let regionIds: string[] | undefined;

      if (isStoreScoped && selectedStore?.storeNumber) {
        storeNumbers = [selectedStore.storeNumber];
      }

      if (filterType === 'stores' && selectedStores.length > 0) {
        storeNumbers = selectedStores;
        regionIds = undefined;
      } else if (filterType === 'regions' && selectedRegions.length > 0) {
        regionIds = selectedRegions;
        storeNumbers = undefined;
      }

      const trainingFilter = {
        franchiseId: selectedFranchise?.franchiseId || 0,
        storeNumbers: null,
        regionIds: null,
      };

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const apiCalls: Promise<any>[] = [];

      if (canViewAuditDashboard) {
        apiCalls.push(
          complianceService.dashboard.getMetrics(storeNumbers, regionIds),
          complianceService.dashboard.getAuditScoreTrends(6, storeNumbers, regionIds),
        );
      }

      if (canViewTraining) {
        apiCalls.push(
          trainingClient.getCompletionTrends(6, trainingFilter).catch(err => {
            console.error('Training Trends Error:', err);
            return [];
          }),
          trainingClient.getDashboardMetrics(trainingFilter).catch(err => {
            console.error('Training Metrics Error:', err);
            return null;
          }),
          trainingClient.getStorePerformance(trainingFilter).catch(err => {
            console.error('Store Performance Error:', err);
            return [];
          }),
        );
      }

      const results = await Promise.all(apiCalls);

      // Extract results based on what was fetched
      let resultIndex = 0;
      let metricsData = null;
      let auditTrendsData: AuditScoreTrend[] = [];
      let trainingTrendsData = [];
      let trainingMetricsData = null;
      let storePerformanceData = [];

      if (canViewAuditDashboard) {
        metricsData = results[resultIndex++];
        auditTrendsData = results[resultIndex++];
      }

      if (canViewTraining) {
        trainingTrendsData = results[resultIndex++];
        trainingMetricsData = results[resultIndex++];
        storePerformanceData = results[resultIndex++];
      }

      setMetrics(metricsData);
      setAuditTrends(auditTrendsData);
      setTrainingTrends(trainingTrendsData);
      setTrainingMetrics(trainingMetricsData);
      setStorePerformance(storePerformanceData);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('dashboard.failedToLoadDashboard'));
    } finally {
      if (isInitial) {
        setLoading(false);
      }
    }
  };

  const handleFilterTypeChange = (newType: 'all' | 'stores' | 'regions') => {
    setFilterType(newType);
    setSelectedStores([]);
    setSelectedRegions([]);
  };

  const handleStoreSelectionChange = (storeNumbers: string[]) => {
    setSelectedStores(storeNumbers);
  };

  const handleRegionSelectionChange = (regionIds: string[]) => {
    setSelectedRegions(regionIds);
  };

  const getFilterSummary = (): string => {
    if (filterType === 'all') {
      return t('schedules.allStores');
    } else if (filterType === 'stores') {
      if (selectedStores.length === 0) {
        return t('dashboard.selectStores');
      } else if (selectedStores.length === 1) {
        const store = userStoresCurrentTenant.find(s => s.storeNumber === selectedStores[0]);
        return store?.storeName ?? selectedStores[0];
      } else {
        return t('dashboard.storesSelected', { count: selectedStores.length });
      }
    } else {
      if (selectedRegions.length === 0) {
        return t('dashboard.selectRegions');
      } else if (selectedRegions.length === 1) {
        const region = regions.find(r => r.id === selectedRegions[0]);
        return region?.name ?? selectedRegions[0];
      } else {
        return t('dashboard.regionsSelected', { count: selectedRegions.length });
      }
    }
  };

  const getPriorityColor = (priority: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (priority) {
      case 'Critical': return 'error';
      case 'High': return 'warning';
      case 'Medium': return 'info';
      case 'Low': return 'default';
      default: return 'default';
    }
  };

  const getStatusColor = (status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (status) {
      case 'Open': return 'error';
      case 'InProgress': return 'warning';
      case 'Resolved': return 'success';
      case 'Closed': return 'default';
      default: return 'default';
    }
  };

  const filteredActionItems = actionItems.filter(item => {
    // Filter by selected stores/regions if applicable
    if (filterType === 'stores' && selectedStores.length > 0) {
      return selectedStores.includes(item.storeNumber);
    } else if (filterType === 'regions' && selectedRegions.length > 0) {
      const store = stores.find(s => s.storeNumber === item.storeNumber);
      return store !== undefined && selectedRegions.includes(store.region);
    }
    return true;
  });

  const paginatedActionItems = filteredActionItems.slice(
    actionItemsPage * actionItemsPerPage,
    (actionItemsPage + 1) * actionItemsPerPage,
  );

  const maxActionItemPages = () => {
    return Math.ceil(filteredActionItems.length / actionItemsPerPage);
  };

  if (loading) {
    return (
      <PageArea>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </PageArea>
    );
  }

  if (error) {
    return (
      <PageArea>
        <Alert severity="error">{error}</Alert>
      </PageArea>
    );
  }

  return (
    <PageArea>
      {/* Header with Title and Actions */}
      <Stack
        spacing={2}
        direction={isMobile ? 'column' : 'row'}
        justifyContent="space-between"
        alignItems={isMobile ? 'flex-start' : 'center'}
        mb={3}
      >
        <PageTitle
          subTitle={viewMode === 'training' ? t('dashboard.pageSubTitleTraining', 'Training completion and certification insights') : viewMode === 'audits' ? t('dashboard.pageSubTitleAudits', 'Audit performance and action item insights') : t('dashboard.pageSubTitle')}>
          {viewMode === 'training' ? t('dashboard.pageTitleTraining', 'Training Dashboard') : viewMode === 'audits' ? t('dashboard.pageTitleAudits', 'Audits Dashboard') : t('dashboard.pageTitle')}
        </PageTitle>
        <Stack
          direction="row"
          spacing={2}
        >
          {viewMode !== 'training' && (
            <Button
              variant="secondary"
              startIcon={<SvgIcon><AssessmentIcon /></SvgIcon>}
              onClick={() => navigate('/compliance/audits/history')}
            >
              {t('dashboard.viewAudits')}
            </Button>
          )}
          {viewMode !== 'training' && (
            <Button
              variant="primary"
              startIcon={<SvgIcon><AssignmentIcon /></SvgIcon>}
              onClick={() => navigate('/compliance/audits/action-items')}
            >
              {t('actionItems.pageTitle')}
            </Button>
          )}
        </Stack>
      </Stack>

      {/* Filter Controls - only visible to admin/partner users (not store-scoped) */}
      {!isStoreScoped && (
        <Stack
          spacing={4}
          direction={isMobile ? 'column' : 'row'}
          alignItems={isMobile ? 'stretch' : 'center'}
          justifyContent="space-between"
          sx={{ mb: 5, mt: 5 }}
        >
          {/* Left side: Filter controls */}
          <Stack
            direction={isMobile ? 'column' : 'row'}
            spacing={4}
            flex={1}
            alignItems={isMobile ? 'stretch' : 'center'}
          >

            {/* Filter Type Selector */}
            <FormControl
              size="small"
            >
              <Select
                value={filterType}
                onChange={(e) => handleFilterTypeChange(e.target.value as 'all' | 'stores' | 'regions')}
                displayEmpty
              >
                <MenuItem value="all">All Stores</MenuItem>
                <MenuItem value="stores">By Store</MenuItem>
                <MenuItem value="regions">By Region</MenuItem>
              </Select>
            </FormControl>

            {/* Store Selector */}
            {filterType === 'stores' && (
              <FormControl
                size="small"
                sx={{ minWidth: 200, flex: 1, maxWidth: 300 }}
              >
                <Select
                  multiple
                  value={selectedStores}
                  onChange={(e) => handleStoreSelectionChange(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                  displayEmpty
                  renderValue={(selected) => {
                    if (selected.length === 0) return 'Select stores...';
                    if (selected.length === 1) {
                      const store = userStoresCurrentTenant.find(s => s.storeNumber === selected[0]);
                      return store?.storeName ?? selected[0];
                    }
                    return `${selected.length} stores selected`;
                  }}
                >
                  {userStoresCurrentTenant.map((store) => (
                    <MenuItem
                      key={store.storeNumber}
                      value={store.storeNumber}
                    >
                      <Checkbox checked={selectedStores.indexOf(store.storeNumber ?? '') > -1} />
                      <ListItemText
                        primary={store.storeName}
                        secondary={store.storeNumber}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Region Selector */}
            {filterType === 'regions' && (
              <FormControl
                size="small"
                sx={{ minWidth: 200, flex: 1, maxWidth: 300 }}
              >
                <Select
                  multiple
                  value={selectedRegions}
                  onChange={(e) => handleRegionSelectionChange(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                  displayEmpty
                  renderValue={(selected) => {
                    if (selected.length === 0) return 'Select regions...';
                    if (selected.length === 1) {
                      const region = regions.find(r => r.id === selected[0]);
                      return region?.name ?? selected[0];
                    }
                    return `${selected.length} regions selected`;
                  }}
                >
                  {regions.map((region) => (
                    <MenuItem
                      key={region.id}
                      value={region.id}
                    >
                      <Checkbox checked={selectedRegions.indexOf(region.id) > -1} />
                      <ListItemText
                        primary={region.name}
                        secondary={`${region.storeCount} ${region.storeCount === 1 ? 'store' : 'stores'}`}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Stack>

          {/* Right side: Summary chip */}
          {!isMobile && (
            <Chip
              icon={<StoreIcon />}
              label={getFilterSummary()}
              color="primary"
              variant="outlined"
              sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}
            />
          )}
        </Stack>
      )}

      {/* Overall Compliance Health and Key Metrics */}
      {showAuditSections && metrics && (
        <Section style={{ paddingLeft: 8, paddingRight: 8, marginBottom: 8, paddingBottom: 8 }}>
          <Box sx={{ padding: 8 }}>
            {/* Overall Compliance Health */}
            <Box sx={{ mb: 5 }}>
              <HealthScoreCard
                score={metrics.overallComplianceScore}
                riskLevel={metrics.riskLevel}
                criticalIssues={metrics.criticalIssuesCount}
              />
            </Box>
          </Box>
        </Section>
      )}
      {/* All Key Metrics with Trends */}
      <Grid
        container
        spacing={4}
      >
        {showAuditSections && metrics && (
          <>
            <Grid
              item
              xs={12}
              sm={6}
              md={6}
              lg={3}
              xl={2}
            >
              <MetricCard
                title={t('dashboard.auditCompletion')}
                value={`${metrics.auditCompletionRate.toFixed(0)}%`}
                subtitle={`${metrics.completedAuditsThisMonth} of ${metrics.totalAuditsThisMonth} this month`}
                icon={<AssessmentIcon fontSize="medium" />}
                color="#1976d2"
                trend={metrics.auditScoreTrend}
                trendLabel="vs last month"
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
              md={6}
              lg={3}
              xl={2}
            >
              <MetricCard
                title={t('dashboard.averageAuditScore')}
                value={`${metrics.averageAuditScore.toFixed(1)}%`}
                subtitle={'All completed'}
                icon={<CheckCircleIcon fontSize="medium" />}
                color="#2e7d32"
                trend={metrics.auditScoreTrend}
                trendLabel="improvement"
              />
            </Grid>
          </>
        )}
        {showTrainingSections && (
          <Grid
            item
            xs={12}
            sm={6}
            md={6}
            lg={3}
            xl={2}
          >
            <MetricCard
              title={t('dashboard.trainingProgress')}
              value={
                `${trainingMetrics?.totalAssignments
                  ? Math.round((trainingMetrics.completedAssignments / trainingMetrics.totalAssignments) * 100)
                  : 0
                }%`}
              subtitle={`${trainingMetrics?.completedAssignments ?? 0} of ${trainingMetrics?.totalAssignments ?? 0} completed`}
              icon={<SchoolIcon fontSize="medium" />}
              color="#ed6c02"
              trend={trainingMetrics?.completionRateTrend ?? undefined}
              trendLabel="vs last month"
            />
          </Grid>
        )}
        {showAuditSections && metrics && (
          <Grid
            item
            xs={12}
            sm={6}
            md={6}
            lg={3}
            xl={2}
          >
            <MetricCard
              title={t('dashboard.openActionItems')}
              value={metrics.openActionItems}
              subtitle={metrics.criticalActionItems > 0 ? `${metrics.criticalActionItems} critical` : `${metrics.actionItemsCompletedThisWeek} completed this week`}
              icon={<WarningIcon fontSize="medium" />}
              color={metrics.criticalActionItems > 0 ? '#d32f2f' : '#1976d2'}
              trend={metrics.actionItemsTrend}
              trendLabel="change"
            />
          </Grid>
        )}

        {showTrainingSections && (
          <Grid
            item
            xs={12}
            sm={6}
            md={6}
            lg={3}
            xl={2}
          >
            <MetricCard
              title={t('dashboard.bestPerformingStore')}
              value={storePerformance?.length > 0 ? storePerformance.sort((a, b) => b.completionRate - a.completionRate)[0]?.storeName : 'N/A'}
              subtitle={storePerformance?.length > 0 ? `${storePerformance.sort((a, b) => b.completionRate - a.completionRate)[0]?.completionRate.toFixed(1)}% completion` : 'No data'}
              icon={<StoreIcon />}
              color="#2e7d32"
            />
          </Grid>
        )}
        {showTrainingSections && (
          <Grid
            item
            xs={12}
            sm={6}
            md={6}
            lg={3}
            xl={2}
          >
            <MetricCard
              title={t('dashboard.needsSupport')}
              value={storePerformance?.length > 0 ? storePerformance.sort((a, b) => a.completionRate - b.completionRate)[0]?.storeName : 'N/A'}
              subtitle={storePerformance?.length > 0 ? `${storePerformance.sort((a, b) => a.completionRate - b.completionRate)[0]?.completionRate.toFixed(1)}% completion` : 'No data'}
              icon={<StoreIcon />}
              color="#ed6c02"
            />
          </Grid>
        )}
        {showTrainingSections && (
          <Grid
            item
            xs={12}
            sm={6}
            md={6}
            lg={3}
            xl={2}
          >
            <MetricCard
              title={t('dashboard.certificatesExpiring')}
              value={trainingMetrics?.certificatesExpiringSoon ?? 0}
              subtitle={t('dashboard.within30Days')}
              icon={<SpeedIcon />}
              color="#f57c00"
            />
          </Grid>
        )}
        {showTrainingSections && (
          <Grid
            item
            xs={12}
            sm={6}
            md={6}
            lg={3}
            xl={2}
          >
            <MetricCard
              title={t('dashboard.avgTrainingScore')}
              value={trainingMetrics?.averageScore ? `${trainingMetrics.averageScore.toFixed(1)}%` : 'N/A'}
              subtitle={t('dashboard.allModules')}
              icon={<SchoolIcon />}
              color="#1976d2"
            />
          </Grid>
        )}
      </Grid>

      {/* Action Items */}
      {showAuditSections && (
        <Section style={{ mb: 5 }}>
          <Box
            sx={{
              p: 5,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                variant="textMD"
                fontWeight={600}
              >
                {t('dashboard.actionItems')}
              </Typography>
              <Pill text={t('dashboard.actionItemsCount', { count: filteredActionItems.length })} />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
            <TableContainer>
              <Table sx={{ minWidth: 600, maxHeight: 400, tableLayout: 'fixed' }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: '35%' }}>{t('dashboard.title')}</TableCell>
                    <TableCell sx={{ width: '20%' }}>{t('common.assignedTo')}</TableCell>
                    <TableCell sx={{ width: '15%' }}>{t('common.priority')}</TableCell>
                    <TableCell sx={{ width: '15%' }}
                      align="center">{t('common.status')}</TableCell>
                    <TableCell sx={{ width: '15%' }}>{t('common.dueDate')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedActionItems.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        align="center"
                        sx={{ py: 8 }}
                      >
                        <Typography
                          variant="textMD"
                          color="textSecondary"
                        >
                          {t('dashboard.noActionItemsFound')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedActionItems.map((item) => (
                      <TableRow
                        key={item.id}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: theme.palette.custom.gray[50],
                          },
                        }}
                        onClick={() => navigate(`/compliance/audits/action-items/${item.id}`, { state: { from: 'dashboard' } })}
                      >
                        <ScrollableTableCell>
                          <Box>
                            <Box
                              sx={{
                                fontSize: '0.75rem',
                                color: theme.palette.custom.gray[500],
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {item.auditName || '-'}
                            </Box>
                            <Box sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}>{item.title}</Box>
                          </Box>
                        </ScrollableTableCell>
                        <TableCell>
                          <Box>
                            <Box
                              sx={{
                                fontSize: '0.75rem',
                                color: theme.palette.custom.gray[500],
                                maxWidth: 200,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {item.storeName}
                            </Box>
                            <Box sx={{ fontWeight: 500 }}>{item.assignedToUserName}</Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={item.priority}
                            color={getPriorityColor(item.priority)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={item.status}
                            color={getStatusColor(item.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {item.status === 'Closed' && item.resolvedDate
                            ? new Date(item.resolvedDate).toLocaleDateString(localeLanguage)
                            : item.dueDate
                              ? new Date(item.dueDate).toLocaleDateString(localeLanguage)
                              : '-'}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <CustomPagination
              page={actionItemsPage}
              setPage={setActionItemsPage}
              maxPages={maxActionItemPages()}
              breakpointForChangeDisplay={120000}
              useNoDataMessage={false}
            />
          </Box>
        </Section>
      )}
      {/* Charts */}
      <Grid
        container
        spacing={5}
        sx={{ mb: 5 }}
      >
        {/* Audit Score Trends */}
        {showAuditSections && (
          <Grid
            item
            xs={12}
            md={6}
          >
            <Section style={{ padding: 5, marginBottom: 5 }}>
              <Typography
                variant="textMD"
                component="div"
                fontWeight={600}
                sx={{ marginBottom: 5 }}
              >
                {t('dashboard.auditScoreTrendsTitle')}
              </Typography>
              <ResponsiveContainer
                width="100%"
                height={300}
              >
                <LineChart data={auditTrends}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis domain={[0, 100]} />
                  <Tooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="averageScore"
                    stroke="#1976d2"
                    strokeWidth={2}
                    name="Average Score (%)"
                  />
                </LineChart>
              </ResponsiveContainer>
              <Box
                sx={{
                  fontSize: '12px',
                  color: theme.palette.custom.gray[600],
                  textAlign: 'center',
                  mt: 1,
                }}
              >
                Total audits conducted: {auditTrends?.reduce((sum, t) => sum + t.auditCount, 0) ?? 0}
              </Box>
            </Section>
          </Grid>
        )}

        {/* Training Completion Trends */}
        {showTrainingSections && (
          <Grid
            item
            xs={12}
            md={6}
          >
            <Section style={{ padding: 5, marginBottom: 5 }}>
              <Typography
                variant="textMD"
                component="div"
                fontWeight={600}
                sx={{ marginBottom: 5 }}
              >
                {t('dashboard.trainingCompletionTrendsTitle')}
              </Typography>
              <ResponsiveContainer
                width="100%"
                height={300}
              >
                <LineChart data={trainingTrends}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis domain={[0, 100]} />
                  <Tooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="completionRate"
                    stroke="#2e7d32"
                    strokeWidth={2}
                    name="Completion Rate (%)"
                  />
                </LineChart>
              </ResponsiveContainer>
              <Box
                sx={{
                  fontSize: '12px',
                  color: theme.palette.custom.gray[600],
                  textAlign: 'center',
                  mt: 1,
                }}
              >
                Total completed: {trainingTrends?.reduce((sum, t) => sum + t.completedCount, 0) ?? 0} / {trainingTrends?.reduce((sum, t) => sum + t.assignedCount, 0) ?? 0}
              </Box>
            </Section>
          </Grid>
        )}
      </Grid>
    </PageArea>
  );
};

export default ComplianceDashboard;